import { datadogLogs } from '@datadog/browser-logs'
import { PanelSupplierCode } from 'model/common'
import { coreRespondentUrl } from 'utils/coreRespondentUrl'
import { API_DISTRIBUTION_FACET } from 'utils/datadog'
import { RespondentEvent } from 'model/respondentStatus'
import { shouldUseNewApiEndpoints } from 'utils/featureFlags'
import getPersistedRespondentChoices from 'utils/getPersistedRespondentChoices'
import { usePatchCallbackWithRetry } from './usePatchCallback'
import { useQuestionnaireParams } from './useQuestionnaireParams'
import { QualityCheckResult } from './usePerformQualityCheck'
import useSendRespondentStatus from './useSendRespondentStatus'

export type ApiRespondentStatus =
  | 'inprogress'
  | 'complete'
  | 'qualityterminate'
  | 'screenout'
  | 'quotafull'
  | 'timeout'

interface UpdateRespondentStatusPayload {
  panel_supplier_code: PanelSupplierCode
  supplier_respondent_id: string
  status: ApiRespondentStatus
  meta_data: Record<string, unknown>
  force?: boolean
}

/**
 * Maps RespondentEvent enum to API status string
 */
const mapRespondentEventToApiStatus = (
  event: RespondentEvent
): ApiRespondentStatus => {
  switch (event) {
    case RespondentEvent.Start:
      return 'inprogress'
    case RespondentEvent.Complete:
      return 'complete'
    case RespondentEvent.QualityTerminate:
      return 'qualityterminate'
    case RespondentEvent.ScreenOut:
      return 'screenout'
    case RespondentEvent.QuotaFull:
      return 'quotafull'
    case RespondentEvent.NotFound:
      return 'timeout'
    default:
      return 'timeout'
  }
}

// Not all 400s mean "blocked" — only those where the backend explicitly
// rejects the status update (e.g. survey already completed or respondent
// in a terminal state). We check the message prefix to distinguish from
// other 400 errors (e.g. validation failures) which should still throw.
function isBlockedStatusError(err: unknown): boolean {
  const response = (err as any)?.response
  return (
    response?.status === 400 &&
    typeof response?.data?.message === 'string' &&
    response.data.message.startsWith('Blocked')
  )
}

const useUpdateRespondent = () => {
  const mandatoryQueryParams = useQuestionnaireParams()
  const patch = usePatchCallbackWithRetry<UpdateRespondentStatusPayload>()
  const updateRespondentLegacy = useSendRespondentStatus()

  const updateRespondent = async (
    respondentStatus: RespondentEvent,
    qualityCheckResult: QualityCheckResult
  ): Promise<{ blocked: boolean }> => {
    datadogLogs.logger.info('Routing update respondent to new endpoint', {
      apiUsed: API_DISTRIBUTION_FACET.NEW
    })

    const payload: UpdateRespondentStatusPayload = {
      panel_supplier_code:
        mandatoryQueryParams.panelSupplier as PanelSupplierCode,
      supplier_respondent_id: mandatoryQueryParams.respondentId,
      status: mapRespondentEventToApiStatus(respondentStatus),
      meta_data: { qualityCheckResult }
    }

    const url = coreRespondentUrl(
      `/surveys/${mandatoryQueryParams.surveyId}/respondents/supplier/${mandatoryQueryParams.respondentId}`
    )

    const persistedRespondentChoices = getPersistedRespondentChoices(
      mandatoryQueryParams.respondentId,
      mandatoryQueryParams.surveyId
    )

    try {
      await patch(payload, url)
      datadogLogs.logger.info(
        `Respondent status updated: ${respondentStatus}`,
        {
          responseChoices: persistedRespondentChoices
        }
      )
      return { blocked: false }
    } catch (err) {
      const blocked = isBlockedStatusError(err)
      datadogLogs.logger[blocked ? 'warn' : 'error'](
        blocked
          ? 'Update respondent returned 400 (terminal status)'
          : 'Network Failure: Update respondent failed',
        {
          responseChoices: persistedRespondentChoices,
          err,
          payload,
          payloadSize: JSON.stringify(payload).length
        }
      )
      if (!blocked) throw err
      return { blocked: true }
    }
  }

  return async (
    respondentStatus: RespondentEvent,
    qualityCheckResult: QualityCheckResult
  ): Promise<{ blocked: boolean }> => {
    if (!mandatoryQueryParams) {
      // NOTE: should never get here
      datadogLogs.logger.error(
        'Update respondent failed. Mandatory params missing. Ensure `sid`, `rid` and `ps` are specified.'
      )
      return { blocked: false }
    }

    const useNewApi = shouldUseNewApiEndpoints(
      mandatoryQueryParams.respondentId
    )

    if (useNewApi) {
      return updateRespondent(respondentStatus, qualityCheckResult)
    }

    // Use legacy endpoint via useSendRespondentStatus
    return updateRespondentLegacy(respondentStatus, qualityCheckResult)
  }
}

export default useUpdateRespondent
